import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Calendar, Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import {
  StepFormContainer,
  StepOneTypeSelection,
  StepTwoOrganizerSelection,
  StepThreeInformationSection,
  StepFourReview,
  StepFourConferenceTickets,
} from "./components";
import { transformConferenceTradeMissionPayload } from "./utils/payloadTransformer";
import conferenceTradeMissionService from "../../../../api/ConferenceTradeMissionService";

const CreateConferenceTradeMission = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [eventType, setEventType] = useState(null);
  const [sourcePage, setSourcePage] = useState(null);
  const [formData, setFormData] = useState({
    organizer: null,
    // Step 3 data
    eventName: "",
    eventStartDate: "",
    eventEndDate: "",
    eventStartTime: "",
    eventEndTime: "",
    venueName: "",
    venueStreetAddress: "",
    country: "",
    city: "",
    venueGoogleLocation: "",
    thumbnail: null,
    featuredImages: [],
    aboutEvent: "",
    performers: [],
    sponsors: [],
    itinerary: [],
    // Conditional fields
    totalParticipantSlots: "",
    motivationalQuestions: [],
    // Conference ticket fields
    overallSlots: "",
    ticketTypes: [],
  });

  const totalSteps = eventType === "conference" ? 5 : 4;
  const stepTitles = [
    "Select Type",
    "Select Organizer",
    eventType === "conference"
      ? "Conference Information"
      : eventType === "trade_mission"
      ? "Trade Mission Information"
      : "Information",
    eventType === "conference" ? "Conference Tickets" : "Review & Submit",
    eventType === "conference" ? "Review & Submit" : "",
  ].filter(Boolean);

  const handleTypeSelect = (type) => {
    setEventType(type);
    setFormData((prev) => ({
      ...prev,
      totalParticipantSlots: "",
      motivationalQuestions: [],
      overallSlots: "",
      ticketTypes: [],
    }));
  };

  const handleStepOneNext = () => {
    if (eventType) {
      setCurrentStep(2);
    } else {
      toast.error("Please select a type");
    }
  };

  const handleOrganizerSelect = (organizer) => {
    setFormData((prev) => ({
      ...prev,
      organizer,
    }));
  };

  const handleStepTwoNext = () => {
    if (formData.organizer) {
      setCurrentStep(3);
    } else {
      toast.error("Please select an organizer");
    }
  };

  const handleStepTwoBack = () => {
    setCurrentStep(1);
  };

  const handleStepThreeNext = () => {
    if (eventType === "conference") {
      setCurrentStep(4);
    } else {
      setCurrentStep(4);
    }
  };

  const handleStepFourNext = () => {
    if (eventType === "conference") {
      setCurrentStep(5);
    }
  };

  const handleStepFourBack = () => {
    if (eventType === "conference") {
      setCurrentStep(3); 
    } else {
      setCurrentStep(3); 
    }
  };

  const handleStepThreeBack = () => {
    setCurrentStep(2);
  };

  const handleStepFiveBack = () => {
    if (eventType === "conference") {
      setCurrentStep(4);
    }
  };

  const handleSubmitEvent = async (data) => {
    const payload = transformConferenceTradeMissionPayload(data, eventType);

    if (!payload.thumbnail) {
      toast.error("Please upload a thumbnail image");
      return;
    }

    try {
      setIsLoading(true);
      const response =
        await conferenceTradeMissionService.createConferenceTradeMission(
          payload
        );

      if (response.data.status_code === 200) {
        toast.success(
          response.data.reason ||
            `${
              eventType === "conference" ? "Conference" : "Trade Mission"
            } created successfully!`
        );
        navigate("/admin/media/conference");
      } else {
        toast.error(
          response.data?.reason ||
            `Failed to create ${
              eventType === "conference" ? "conference" : "trade mission"
            }`
        );
      }
    } catch (err) {
      toast.error(
        err.message ||
          `An error occurred while creating ${
            eventType === "conference" ? "conference" : "trade mission"
          }`
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormDataUpdate = (updatedData) => {
    setFormData((prev) => ({
      ...prev,
      ...updatedData,
    }));
  };

  // Auto-select event type based on source page
  useEffect(() => {
    // Check if eventType is passed in location state
    if (location.state?.eventType) {
      setEventType(location.state.eventType);
      setSourcePage(location.state.from);
    } else {
      // Try to determine from referrer or URL
      const referrer = document.referrer;
      if (referrer.includes("/admin/media/conferences")) {
        setEventType("conference");
        setSourcePage("/admin/media/conferences");
      } else if (referrer.includes("/admin/media/trade-missions")) {
        setEventType("trade_mission");
        setSourcePage("/admin/media/trade-missions");
      }
    }
  }, [location.state]);

  // Auto-advance to step 2 if event type is pre-selected
  useEffect(() => {
    if (eventType && currentStep === 1) {
      setCurrentStep(2);
    }
  }, [eventType, currentStep]);

  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentStep]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="w-12 h-12 text-purple-600 animate-spin" />
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <div className="px-6 py-4 mb-6">
        <button
          onClick={() => {
            // Navigate back to source page if available, otherwise use eventType
            if (sourcePage) {
              navigate(sourcePage);
            } else if (eventType === "conference") {
              navigate("/admin/media/conferences");
            } else if (eventType === "trade_mission") {
              navigate("/admin/media/trade-missions");
            } else {
              navigate("/admin/media/conference");
            }
          }}
          className="flex items-center mb-3 text-gray-600 transition-colors hover:text-gray-900 group"
        >
          <ArrowLeft className="mr-2 w-4 h-4 transition-transform group-hover:-translate-x-1" />
          <span className="text-sm font-medium">
            {eventType === "conference"
              ? "Back to Conferences"
              : eventType === "trade_mission"
              ? "Back to Trade Missions"
              : "Back to Conferences & Trade Missions"}
          </span>
        </button>

        <div className="flex items-center space-x-3">
          <div className="p-2 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow">
            <Calendar className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">
              Create{" "}
              {eventType === "conference"
                ? "Conference"
                : eventType === "trade_mission"
                ? "Trade Mission"
                : "New Event"}
            </h1>
            <p className="mt-0.5 text-xs text-gray-600">
              Follow the steps to create a new{" "}
              {eventType === "conference"
                ? "conference"
                : eventType === "trade_mission"
                ? "trade mission"
                : "event"}
            </p>
          </div>
        </div>
      </div>

      <StepFormContainer
        currentStep={currentStep}
        totalSteps={totalSteps}
        stepTitles={stepTitles}
        showProgress={true}
      >
        {/* Step Content */}
        {currentStep === 1 && (
          <StepOneTypeSelection
            selectedType={eventType}
            onSelect={handleTypeSelect}
            onNext={handleStepOneNext}
          />
        )}

        {currentStep === 2 && (
          <StepTwoOrganizerSelection
            selectedOrganizer={formData.organizer}
            onSelect={handleOrganizerSelect}
            onNext={handleStepTwoNext}
            onBack={handleStepTwoBack}
          />
        )}

        {currentStep === 3 && (
          <StepThreeInformationSection
            formData={formData}
            onUpdate={handleFormDataUpdate}
            onBack={handleStepThreeBack}
            onNext={handleStepThreeNext}
            eventType={eventType}
          />
        )}

        {currentStep === 4 && eventType === "conference" && (
          <StepFourConferenceTickets
            formData={formData}
            onUpdate={handleFormDataUpdate}
            onBack={handleStepFourBack}
            onNext={handleStepFourNext}
          />
        )}

        {currentStep === (eventType === "conference" ? 5 : 4) && (
          <StepFourReview
            formData={formData}
            onBack={eventType === "conference" ? handleStepFiveBack : handleStepFourBack}
            onSubmit={handleSubmitEvent}
            eventType={eventType}
          />
        )}
      </StepFormContainer>
    </>
  );
};

export default CreateConferenceTradeMission;
